import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { type EventTaskSummary, type RoomTaskSummary } from "@crowdcoursing/api/graphql";
import { type ProspectTaskDto } from "@crowdcoursing/api/openapi";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import { alpha, Box, IconButton, Paper, Stack, styled, Typography } from "@mui/material";
import { type ReactNode, useRef, useState } from "react";
import Slider from "react-slick";

import { type TaskType } from "~hooks/useTaskDashboard";

import { PageHeader } from "../../atoms/PageHeader/PageHeader";
import { TaskCard } from "../../atoms/TaskCard/TaskCard";
import { TaskContent } from "../../molecules/TaskContent/TaskContent";
import { type CarouselTaskItem, combineTasks } from "./taskUtils";

// Centralized strings for internationalization
const tAllDone = "All Done! 🎉";
const tTasksCompleted = "You have completed all your tasks. Great job!";

export interface TaskDashboardTemplateProps {
  currentTaskType: TaskType;
  onTaskTypeChange: (taskType: TaskType) => void;
  salesTasks: {
    myTasks: ProspectTaskDto[];
    tasksDueSoon: ProspectTaskDto[];
    isLoading: boolean;
    handleCompleteTask: (taskId: string) => void;
    handleDeleteTask: (taskId: string) => void;
  };
  roomTasks?: {
    myTasks: RoomTaskSummary[];
    isLoading: boolean;
  };
  eventTasks?: {
    myTasks: EventTaskSummary[];
    isLoading: boolean;
  };
  onTaskSelect?: (task: CarouselTaskItem) => void;
  selectedTask?: CarouselTaskItem | null;
  taskContentArea?: ReactNode;
}

export const TaskDashboardTemplate = ({
  currentTaskType,
  onTaskTypeChange,
  salesTasks,
  roomTasks,
  eventTasks,
  onTaskSelect,
  taskContentArea,
  selectedTask,
}: TaskDashboardTemplateProps) => {
  // Track which tasks are currently being completed (by their ID)
  const [completingTaskIds, setCompletingTaskIds] = useState<string[]>([]);
  const sliderRef = useRef<Slider>(null);

  // Check if a specific task is currently being completed
  const isTaskCompleting = (taskId?: string) => {
    return taskId ? completingTaskIds.includes(taskId) : false;
  };

  // Handler for completing tasks based on task type
  const handleCompleteTask = (task: CarouselTaskItem) => async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card selection

    if (!task.id) {
      return;
    }

    // Add this task ID to the completing list
    setCompletingTaskIds((prev) => [...prev, task.id ?? ""]);

    try {
      switch (task.type) {
        case "sales":
          await salesTasks.handleCompleteTask(task.id);
          break;
        case "room":
          // Will be implemented when room tasks are added
          console.log("Complete room task:", task.id);
          // Simulate API call for demo purposes
          await new Promise((resolve) => setTimeout(resolve, 1000));
          break;
        case "event":
          // Will be implemented when event tasks are added
          console.log("Complete event task:", task.id);
          // Simulate API call for demo purposes
          await new Promise((resolve) => setTimeout(resolve, 1000));
          break;
        default:
          console.error("Unknown task type:", task.type);
      }
    } catch (error) {
      console.error("Error completing task:", error);
    } finally {
      // Remove this task ID from the completing list
      setCompletingTaskIds((prev) => prev.filter((id) => id !== task.id));
    }
  };

  // Combine all tasks for the carousel using the utility function
  const allTasks: CarouselTaskItem[] = combineTasks(
    salesTasks.myTasks || [],
    roomTasks?.myTasks || [],
    eventTasks?.myTasks || [],
  );

  // Check if there are no tasks at all
  const hasNoTasks = allTasks.length === 0;

  const handleTaskSelect = (task: CarouselTaskItem) => {
    // Change the task type if needed
    if (task.type !== currentTaskType) {
      onTaskTypeChange(task.type);
    }

    // Call the onTaskSelect callback if provided
    if (onTaskSelect) {
      onTaskSelect(task);
    }
  };

  // Find the index of the selected task
  const selectedIndex = selectedTask ? allTasks.findIndex((task) => task.id === selectedTask.id) : -1;
  const initialSlide = selectedIndex >= 0 ? Math.floor(selectedIndex / 3) * 3 : 0;

  // Slider settings
  const settings = {
    arrows: false, // We'll use our custom arrows
    dots: true,
    dotsClass: "slick-dots slick-single-line-dots", // Custom class for styling
    infinite: false,
    initialSlide,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          initialSlide: selectedIndex >= 0 ? Math.floor(selectedIndex / 2) * 2 : 0,
          slidesToScroll: 2,
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          initialSlide: selectedIndex,
          slidesToScroll: 1,
          slidesToShow: 1,
          swipe: true,
          swipeToSlide: true,
          touchMove: true,
          touchThreshold: 5, // More sensitive on small screens
        },
      },
    ],
    slidesToScroll: 3,
    slidesToShow: 3,
    speed: 500,
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 10,
  };

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  return (
    <Box
      sx={{
        maxWidth: "100%",
        overflowX: "hidden",
        p: { sm: 2, xs: 1 },
      }}
    >
      <Stack spacing={2} sx={{ width: "100%" }}>
        <PageHeader title="Task Dashboard" icon={<ChecklistRoundedIcon data-variant="page-header" />} />

        {hasNoTasks ? (
          <AllDoneBanner>
            <Box sx={{ alignItems: "center", display: "flex", gap: 1.5, justifyContent: "center" }}>
              <Typography variant="h4" component="h2" color="primary.main" fontWeight="bold">
                {tAllDone}
              </Typography>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              {tTasksCompleted}
            </Typography>
          </AllDoneBanner>
        ) : (
          <CarouselSection>
            {/* Full-width container for the carousel with edge arrows */}
            <FullWidthContainer>
              {/* Left arrow positioned at the left edge */}
              <EdgeArrow onClick={goToPrev} aria-label="Previous task" className="left-arrow">
                <ArrowBackIosNewIcon />
              </EdgeArrow>

              {/* Center container for the carousel content */}
              <CenterContainer>
                <StyledSlider ref={sliderRef} {...settings}>
                  {allTasks.map((task) => (
                    <div key={task.id} style={{ height: "100%", padding: "0 8px" }}>
                      <TaskCard
                        onClick={() => handleTaskSelect(task)}
                        isSelected={task.id === selectedTask?.id}
                        onComplete={handleCompleteTask(task)}
                        isCompleting={isTaskCompleting(task.id)}
                      >
                        <TaskContent task={task} />
                      </TaskCard>
                    </div>
                  ))}
                </StyledSlider>
              </CenterContainer>

              {/* Right arrow positioned at the right edge */}
              <EdgeArrow onClick={goToNext} aria-label="Next task" className="right-arrow">
                <ArrowForwardIosIcon />
              </EdgeArrow>
            </FullWidthContainer>
          </CarouselSection>
        )}

        {/* Task content area - will be implemented in the future */}
        {taskContentArea && <Box sx={{ mt: 3 }}>{taskContentArea}</Box>}
      </Stack>
    </Box>
  );
};

// Styled components for the carousel
const StyledSlider = styled(Slider)(({ theme }) => ({
  ".slick-dots": {
    "& li": {
      margin: "0 3px",
      width: "auto",
    },
    "& li button:before": {
      fontSize: "10px",
      opacity: 0.5,
    },
    "& li.slick-active button:before": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
    bottom: "-30px",
    display: "flex !important",
    flexWrap: "nowrap",
    justifyContent: "center",
    overflow: "auto",
    padding: theme.spacing(1, 0),
    whiteSpace: "nowrap",
  },
  ".slick-list": {
    overflow: "hidden",
  },
  ".slick-slide": {
    height: "auto",
    padding: theme.spacing(1),
  },
  ".slick-track": {
    alignItems: "stretch",
    display: "flex",
    margin: "0 auto",
  },
  width: "100%",
}));

const CarouselSection = styled(Box)(({ theme }) => ({
  "&::after": {
    backgroundColor: theme.palette.divider,
    content: '""',
    height: "1px",
    left: 0,
    position: "absolute",
    right: 0,
  },
  margin: theme.spacing(3, 0, 6),
  position: "relative",
}));

// Full-width container that spans the entire viewport width
const FullWidthContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  left: "50%",
  marginLeft: "-50vw",
  marginRight: "-50vw",
  position: "relative",
  right: "50%",
  width: "100vw",
}));

// Center container for the carousel content
const CenterContainer = styled(Box)(({ theme }) => ({
  // Add bottom padding for dots
  boxSizing: "border-box",

  flex: 1,

  margin: "0 auto",

  maxWidth: "1200px",
  padding: theme.spacing(0, 2, 4, 2),
}));

// Edge-positioned arrows
const EdgeArrow = styled(IconButton)(({ theme }) => ({
  "& svg": {
    color: theme.palette.text.primary,
    fontSize: "1.6rem",
  },
  "&.left-arrow": {
    left: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      left: theme.spacing(5),
    },
  },
  "&.right-arrow": {
    right: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      right: theme.spacing(5),
    },
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[6],
  },
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[4],
  height: 50,

  position: "absolute",
  // Hide arrows on mobile devices
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },

  top: "calc(50% - 40px)",

  // Adjust to account for dots
  transform: "translateY(-50%)",

  width: 50,
  zIndex: 10,
}));

// Styled component for the "All Done!" banner
const AllDoneBanner = styled(Paper)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  borderRadius: (theme.shape.borderRadius as number) * 2,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: theme.spacing(3, 0, 6),
  padding: theme.spacing(4),
  position: "relative",
  textAlign: "center",
}));
